import React, { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navigation = ({ 
  activeSection, 
  scrollToSection, 
  language, 
  onLanguageModalOpen, 
  translations 
}) => {
  const t = translations;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleDropdownToggle = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const navigationItems = [
    { 
      id: 'home', 
      label: t.home, 
      hasDropdown: false 
    },
    { 
      id: 'services', 
      label: t.services, 
      hasDropdown: true,
      dropdownItems: [
        { label: 'Bespoke Cakes', action: () => scrollToSection('services') },
        { label: 'Sweet Treats', action: () => scrollToSection('services') },
        { label: 'Event Catering', action: () => scrollToSection('services') }
      ]
    },
    { 
      id: 'about', 
      label: t.about, 
      hasDropdown: false 
    },
    { 
      id: 'contact', 
      label: t.contact, 
      hasDropdown: false 
    },
    { 
      id: 'faq', 
      label: t.faq, 
      hasDropdown: false 
    }
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50">
        {/* Main Navigation Bar */}
        <div className="bg-white/95 backdrop-blur-sm shadow-lg border-b border-pink-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3">
              
              {/* Logo Section */}
              <div className="flex items-center space-x-3">
                <img 
                  src="logo.png" 
                  alt="Sweet Like Nina Logo" 
                  className="w-12 h-12 rounded-full object-cover shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
                />
                <div className=" sm:block">
                  <h1 className="text-xl font-bold text-pink-600 hover:text-pink-700 transition-colors duration-300">
                    Sweet Like Nina
                  </h1>
                  <p className="text-xs text-pink-400 font-medium">Bespoke Cakes & Treats</p>
                </div>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-1">
                {navigationItems.map((item) => (
                  <div key={item.id} className="relative group">
                    <button
                      onClick={() => item.hasDropdown ? handleDropdownToggle(item.id) : scrollToSection(item.id)}
                      className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                        activeSection === item.id 
                          ? 'text-pink-600 bg-pink-50 border-b-2 border-pink-600' 
                          : 'text-gray-700 hover:text-pink-600 hover:bg-pink-50'
                      }`}
                    >
                      {item.label}
                      {item.hasDropdown && (
                        <ChevronDown 
                          className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                            activeDropdown === item.id ? 'rotate-180' : ''
                          }`} 
                        />
                      )}
                    </button>
                    
                    {/* Dropdown Menu */}
                    {item.hasDropdown && activeDropdown === item.id && (
                      <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-pink-100 py-2 z-50">
                        {item.dropdownItems.map((dropdownItem, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              dropdownItem.action();
                              setActiveDropdown(null);
                            }}
                            className="w-full text-left px-4 py-3 text-gray-700 hover:text-pink-600 hover:bg-pink-50 transition-colors duration-200 border-b border-pink-50 last:border-b-0"
                          >
                            {dropdownItem.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Right Section - Language & Mobile Menu */}
              <div className="flex items-center space-x-3">
                {/* Language Switcher */}
                <button
                  onClick={onLanguageModalOpen}
                  className="px-3 py-1.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white text-sm font-medium rounded-full hover:from-pink-600 hover:to-pink-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
                >
                  {language === 'en' ? '🇬🇧 EN' : '🇷🇴 RO'}
                </button>

                {/* Mobile Menu Button */}
                <button
                  onClick={toggleMobileMenu}
                  className="lg:hidden p-2 rounded-lg text-gray-700 hover:text-pink-600 hover:bg-pink-50 transition-colors duration-200"
                >
                  {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={toggleMobileMenu} />
          <div className="fixed top-0 right-0 w-80 h-full bg-white shadow-2xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-3">
                  <img 
                    src="logo.png" 
                    alt="Sweet Like Nina Logo" 
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-pink-600">Sweet Like Nina</h2>
                    <p className="text-xs text-pink-400">Bespoke Cakes & Treats</p>
                  </div>
                </div>
                <button onClick={toggleMobileMenu} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>

              <div className="space-y-2">
                {navigationItems.map((item) => (
                  <div key={item.id}>
                    <button
                      onClick={() => {
                        if (item.hasDropdown) {
                          handleDropdownToggle(item.id);
                        } else {
                          scrollToSection(item.id);
                          setIsMobileMenuOpen(false);
                        }
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl font-medium transition-all duration-200 ${
                        activeSection === item.id 
                          ? 'text-pink-600 bg-pink-50' 
                          : 'text-gray-700 hover:text-pink-600 hover:bg-pink-50'
                      }`}
                    >
                      {item.label}
                      {item.hasDropdown && (
                        <ChevronDown 
                          className={`w-4 h-4 transition-transform duration-200 ${
                            activeDropdown === item.id ? 'rotate-180' : ''
                          }`} 
                        />
                      )}
                    </button>
                    
                    {item.hasDropdown && activeDropdown === item.id && (
                      <div className="ml-4 mt-2 space-y-1">
                        {item.dropdownItems.map((dropdownItem, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              dropdownItem.action();
                              setIsMobileMenuOpen(false);
                              setActiveDropdown(null);
                            }}
                            className="w-full text-left px-4 py-2 text-gray-600 hover:text-pink-600 hover:bg-pink-25 rounded-lg transition-colors duration-200"
                          >
                            {dropdownItem.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex items-center space-x-3">
                <img 
                  src="logo.png" 
                  alt="Sweet Like Nina Logo" 
                  className="w-22 h-22 rounded-full object-cover shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
                />
                <div className="hidden sm:block">
                  <h1 className="text-xl font-bold text-pink-600 hover:text-pink-700 transition-colors duration-300">
                    Sweet Like Nina
                  </h1>
                  <p className="text-xs text-pink-400 font-medium">Bespoke Cakes & Treats</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navigation;